import { apiFetch } from './http';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export interface MenuTag {
  id: string;
  name: string;
}

export interface MenuItemNutrition {
  energy: number;
  proteins: number;
  fats: number;
  carbs: number;
}

export interface MenuItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  image?: string;
  nutrition?: MenuItemNutrition;
  isUnavailable: boolean;
  tags?: MenuTag[];
}

export interface MenuCategory {
  id: string;
  name: string;
  tags?: MenuTag[];
  items: MenuItem[];
}

export interface Establishment {
  id: string;
  name: string;
  address: string;
}

export interface TerminalGroup {
  id: string;
  name: string;
  address: string;
  isAlive?: boolean | null;
}

export interface DeliveryAddress {
  city: string;
  street: string;
  house: string;
  apartment?: string;
  yandexAddressId?: string;
  formattedAddress?: string;
}

interface OrderCreateDto {
  phone: string;
  serviceType: 'DeliveryPickUp' | 'DeliveryByCourier';
  terminalGroupId: string;
  items: { productId: string; amount: number; price: number; comment?: string }[];
  firstName?: string;
  lastName?: string;
  comment?: string;
  address?: DeliveryAddress;
  paymentMethod?: 'cash' | 'online';
  returnUrl?: string;
  failUrl?: string;
  paymentConfirmation?: 'redirect' | 'embedded';
}

export interface CreateOrderResult {
  orderId: string;
  orderNumber: number;
  paymentUrl?: string;
  paymentId?: string;
  confirmationToken?: string;
}

export interface UserOrderListItem {
  id: string;
  orderNumber: number;
  iikoOrderId: string;
  createdAt: string;
  totalAmount: number | null;
  items: unknown;
  serviceType: string;
  paymentMethod?: string;
  paymentStatus?: string;
}

export interface OrderStatusPayload {
  orderId: string;
  orderNumber?: number;
  status: string;
  waitingTime?: number;
  estimatedTime?: number;
}

export interface OperatingStatus {
  isOpen: boolean;
  timezone: string;
  closesAt: string | null;
  nextOpenAt: string | null;
}

function authJsonHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function pickClosedPayload(data: Record<string, unknown>): { message: string; nextOpenAt?: string } {
  const nested =
    typeof data.message === 'object' && data.message !== null && !Array.isArray(data.message)
      ? (data.message as Record<string, unknown>)
      : data;
  const msg =
    typeof nested.message === 'string'
      ? nested.message
      : typeof data.message === 'string'
        ? data.message
        : 'Сейчас нельзя оформить заказ';
  const nextOpenAt =
    typeof nested.nextOpenAt === 'string'
      ? nested.nextOpenAt
      : typeof data.nextOpenAt === 'string'
        ? data.nextOpenAt
        : undefined;
  return { message: msg, nextOpenAt };
}

export const ordersApi = {
  async getOperatingStatus(): Promise<OperatingStatus> {
    const res = await fetch(`${API_URL}/orders/operating-status`, { cache: 'no-store' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { isOpen: true, timezone: 'Europe/Moscow', closesAt: null, nextOpenAt: null };
    }
    return {
      isOpen: !!data.isOpen,
      timezone: typeof data.timezone === 'string' ? data.timezone : 'Europe/Moscow',
      closesAt: typeof data.closesAt === 'string' ? data.closesAt : null,
      nextOpenAt: typeof data.nextOpenAt === 'string' ? data.nextOpenAt : null,
    };
  },

  async getEstablishments(): Promise<Establishment[]> {
    const res = await fetch(`${API_URL}/orders/establishments`);
    if (!res.ok) throw new Error('Failed to fetch establishments');
    const data = await res.json();
    return data.establishments || [];
  },

  async getTerminalGroups(): Promise<TerminalGroup[]> {
    const res = await fetch(`${API_URL}/orders/terminal-groups/status`);
    if (!res.ok) throw new Error('Failed to fetch terminal groups');
    const data = await res.json();
    return data.terminalGroups || [];
  },

  async getMenu(channel: 'delivery' | 'dine-in' = 'delivery'): Promise<MenuCategory[]> {
    const res = await fetch(`${API_URL}/orders/menu?channel=${encodeURIComponent(channel)}`);
    if (!res.ok) throw new Error('Failed to fetch menu');
    const data = await res.json();
    return (data.categories || []).map((cat: any) => ({
      id: cat.id,
      name: cat.name,
      tags: Array.isArray(cat.tags) ? cat.tags : [],
      items: (cat.items || []).map((item: any) => ({
        id: item.itemId,
        name: item.name,
        description: item.description,
        price: item.size?.price ?? 0,
        image: item.size?.image,
        isUnavailable: item.isUnavailable,
        tags: Array.isArray(item.tags) ? item.tags : [],
      })),
    }));
  },

  async getMenuItem(
    id: string,
    channel: 'delivery' | 'dine-in' = 'delivery',
  ): Promise<MenuItem | null> {
    const { MENU_MOCK_MODE, findMockMenuItem } = await import('../data/mockMenu');
    if (MENU_MOCK_MODE && id.startsWith('mock-')) return findMockMenuItem(id);

    const res = await fetch(
      `${API_URL}/orders/menu/${encodeURIComponent(id)}?channel=${encodeURIComponent(channel)}`,
    );
    if (!res.ok) {
      if (MENU_MOCK_MODE) return findMockMenuItem(id);
      return null;
    }
    const data = await res.json();
    const item = data.item;
    return {
      id: item.itemId,
      name: item.name,
      description: item.description,
      price: item.size?.price ?? 0,
      image: item.size?.image,
      nutrition: item.nutrition,
      isUnavailable: item.isUnavailable,
      tags: Array.isArray(item.tags) ? item.tags : [],
    };
  },

  async createOrder(token: string, dto: OrderCreateDto): Promise<CreateOrderResult> {
    const res = await apiFetch(`${API_URL}/orders`, {
      method: 'POST',
      headers: authJsonHeaders(token),
      body: JSON.stringify(dto),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      if (res.status === 403) {
        const { message, nextOpenAt } = pickClosedPayload(data);
        if (nextOpenAt) {
          const t = new Date(nextOpenAt).toLocaleString('ru-RU', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          });
          throw new Error(`${message} · откроемся ${t}`);
        }
        throw new Error(message);
      }
      const msg = Array.isArray(data.message)
        ? data.message.join(', ')
        : typeof data.message === 'string'
          ? data.message
          : 'Не удалось создать заказ';
      throw new Error(msg);
    }
    return {
      orderId: String(data.orderId ?? ''),
      orderNumber: Number(data.orderNumber),
      paymentUrl: typeof data.paymentUrl === 'string' ? data.paymentUrl : undefined,
      paymentId: typeof data.paymentId === 'string' ? data.paymentId : undefined,
      confirmationToken:
        typeof data.confirmationToken === 'string' ? data.confirmationToken : undefined,
    };
  },

  async confirmOrder(orderId: string): Promise<void> {
    const res = await fetch(`${API_URL}/orders/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId }),
    });
    if (!res.ok) throw new Error('Failed to confirm order');
  },

  async getOrderStatus(orderId: string) {
    const res = await fetch(`${API_URL}/orders/${orderId}/status`);
    if (!res.ok) throw new Error('Failed to get order status');
    const data = await res.json();
    return data.status;
  },

  async getMyOrders(token: string): Promise<UserOrderListItem[]> {
    const res = await apiFetch(`${API_URL}/orders/my`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Не удалось загрузить заказы');
    const data = await res.json();
    return data.orders ?? [];
  },

  async syncMyPayment(
    token: string,
    userOrderId: string,
  ): Promise<{ synced: boolean; paymentStatus: string; yookassaStatus?: string }> {
    const enc = encodeURIComponent(userOrderId);
    const res = await apiFetch(`${API_URL}/orders/my/${enc}/sync-payment`, {
      method: 'POST',
      headers: authJsonHeaders(token),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = Array.isArray(data.message)
        ? data.message.join(', ')
        : typeof data.message === 'string'
          ? data.message
          : 'Не удалось проверить оплату';
      throw new Error(msg);
    }
    return {
      synced: !!data.synced,
      paymentStatus: data.paymentStatus ?? 'pending',
      yookassaStatus: data.yookassaStatus,
    };
  },

  async getMyOrderStatus(token: string, orderRef: string): Promise<OrderStatusPayload | null> {
    const enc = encodeURIComponent(orderRef);
    const res = await apiFetch(`${API_URL}/orders/my/${enc}/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Не удалось загрузить статус');
    const data = await res.json();
    return data.status ?? null;
  },

  getMyOrderStatusStreamUrl(orderRef: string, token: string): string {
    const enc = encodeURIComponent(orderRef);
    const u = new URL(`${API_URL}/orders/my/${enc}/status/stream`);
    u.searchParams.set('token', token);
    return u.toString();
  },

  async checkDelivery(address: { city: string; street: string; house: string }) {
    const res = await fetch(`${API_URL}/orders/check-delivery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address }),
    });
    if (!res.ok) throw new Error('Failed to check delivery');
    return res.json();
  },

  async cancelOrder(token: string, userOrderId: string): Promise<void> {
    const enc = encodeURIComponent(userOrderId);
    const res = await apiFetch(`${API_URL}/orders/my/${enc}/cancel`, {
      method: 'POST',
      headers: authJsonHeaders(token),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = Array.isArray(data.message)
        ? data.message.join(', ')
        : typeof data.message === 'string'
          ? data.message
          : 'Не удалось отменить заказ';
      throw new Error(msg);
    }
  },
};
